import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatFormField, MatPrefix, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatError, MatInput } from '@angular/material/input';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { EMPTY, catchError, finalize } from 'rxjs';
import { escapeHtml } from '../../utils/html';
import {
  HasPermissionDirective,
  isHttpErrorResponse,
} from '@ipg/ngx-frontend-toolset';
import { DataTableContainerComponent } from '../../components/data-table-container/data-table-container.component';
import { DialogService } from '../../components/dialog/dialog.service';
import { TableLimitService } from '../../services/table-limit.service';
import { VarResponse, VarService } from '../../services/var.service';

@Component({
  selector: 'app-settings-variables',
  imports: [
    MatButton,
    MatError,
    MatFormField,
    MatIcon,
    MatIconButton,
    MatInput,
    MatPaginator,
    MatPrefix,
    MatSuffix,
    MatSortModule,
    MatTableModule,
    MatTooltip,
    ReactiveFormsModule,
    DataTableContainerComponent,
    HasPermissionDirective,
  ],
  templateUrl: './settings-variables.component.html',
  styleUrl: './settings-variables.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SettingsVariablesComponent {
  private readonly _dialog = inject(DialogService);
  private readonly _fb = inject(NonNullableFormBuilder);
  private readonly _snackbar = inject(MatSnackBar);
  private readonly _varService = inject(VarService);
  private readonly _tableLimit = inject(TableLimitService);

  protected readonly vars = this._varService.vars;
  protected readonly pendingDeletionVars = this._varService.pendingDeletionVars;
  protected readonly showAddForm = signal(false);
  protected readonly pendingExpanded = signal(false);
  protected readonly submitting = signal(false);
  protected readonly keyword = signal<string | null>(null);
  protected readonly page = signal(0);
  protected readonly pageSize = signal(this._tableLimit.getLimit());

  protected readonly displayedColumns = ['key', 'value', 'description', 'actions'];
  protected readonly pendingDisplayedColumns = [
    'key',
    'value',
    'description',
    'deleteAt',
    'actions',
  ];

  /** Currently editing var id, or null */
  protected readonly editingVarId = signal<string | null>(null);

  /** Sort state for main table */
  protected readonly sortActive = signal<string>('key');
  protected readonly sortDirection = signal<'asc' | 'desc' | ''>('asc');

  /** Sort state for pending table */
  protected readonly pendingSortActive = signal<string>('');
  protected readonly pendingSortDirection = signal<'asc' | 'desc' | ''>('');

  /** Filtered vars based on search query, sorted by active sort */
  protected readonly filteredVars = computed(() => {
    const query = this.keyword()?.toLowerCase().trim();
    const allVars = this.vars();

    const filtered = query
      ? allVars.filter(
          (v) =>
            v.key.toLowerCase().includes(query) ||
            v.value.toLowerCase().includes(query) ||
            (v.description?.toLowerCase().includes(query) ?? false)
        )
      : allVars;

    return this._sortVars(filtered, this.sortActive(), this.sortDirection());
  });

  /** Paginated slice of filtered vars */
  protected readonly paginatedVars = computed(() => {
    const vars = this.filteredVars();
    const start = this.page() * this.pageSize();
    return vars.slice(start, start + this.pageSize());
  });

  /** Filtered pending-deletion vars based on search query */
  protected readonly filteredPendingVars = computed(() => {
    const query = this.keyword()?.toLowerCase().trim();
    const allVars = this.pendingDeletionVars();

    const filtered = query
      ? allVars.filter(
          (v) =>
            v.key.toLowerCase().includes(query) ||
            v.value.toLowerCase().includes(query) ||
            (v.description?.toLowerCase().includes(query) ?? false)
        )
      : allVars;

    return this._sortVars(
      filtered,
      this.pendingSortActive(),
      this.pendingSortDirection()
    );
  });

  protected readonly hasNoData = computed(() => this.vars().length === 0);

  protected readonly addForm = this._fb.group({
    key: this._fb.control('', [
      Validators.required,
      Validators.maxLength(50),
      Validators.pattern(/^[a-z][a-z0-9_]*$/),
    ]),
    value: this._fb.control('', [Validators.required, Validators.maxLength(255)]),
    description: this._fb.control('', [Validators.maxLength(255)]),
  });

  protected readonly editForm = this._fb.group({
    value: this._fb.control('', [Validators.required, Validators.maxLength(255)]),
    description: this._fb.control('', [Validators.maxLength(255)]),
  });

  constructor() {
    this._varService.getAll().subscribe();
    this._varService.getPendingDeletionVars().subscribe();
  }

  openAddForm() {
    this.addForm.reset();
    this.showAddForm.set(true);
    this.editingVarId.set(null);
    setTimeout(() => document.getElementById('add-key')?.focus());
  }

  cancelAdd() {
    this.showAddForm.set(false);
  }

  startEdit(v: VarResponse) {
    this.showAddForm.set(false);
    this.editingVarId.set(v.id);
    this.editForm.patchValue({
      value: v.value,
      description: v.description ?? '',
    });
  }

  cancelEdit() {
    this.editingVarId.set(null);
  }

  handleSearch(value: string) {
    this.keyword.set(value.trim() || null);
    this.page.set(0);
  }

  clearSearch() {
    this.keyword.set(null);
    this.page.set(0);
  }

  handlePageChange({ pageIndex, pageSize }: PageEvent) {
    this._tableLimit.saveLimit(pageSize);
    this.pageSize.set(pageSize);
    this.page.set(pageIndex);
  }

  handleAdd() {
    if (this.addForm.invalid || this.submitting()) return;

    this.submitting.set(true);
    const { key, value, description } = this.addForm.getRawValue();

    this._varService
      .createVar({
        key,
        value,
        description: description || undefined,
      })
      .pipe(
        catchError((error) => {
          const { title, message } = this._getErrorAlert(
            error,
            'Er is iets misgegaan bij het aanmaken van de variabele.',
            {
              VAR_KEY_DUPLICATE: {
                title: 'Dubbele sleutel',
                message: `Er bestaat al een variabele met de sleutel <b>[${escapeHtml(key)}]</b>. Kies een andere sleutel.`,
              },
            }
          );
          this._dialog.alert({ title, message, confirmText: 'OK' });
          return EMPTY;
        }),
        finalize(() => this.submitting.set(false))
      )
      .subscribe({
        next: () => {
          this._snackbar.open('Variabele aangemaakt', 'OK');
          this.showAddForm.set(false);
        },
      });
  }

  handleEdit() {
    const varId = this.editingVarId();
    if (!varId || this.editForm.invalid || this.submitting()) return;

    this.submitting.set(true);
    const { value, description } = this.editForm.getRawValue();

    this._varService
      .updateVar(varId, {
        value,
        description: description || undefined,
      })
      .pipe(
        catchError((error) => {
          const { title, message } = this._getErrorAlert(
            error,
            'Er is iets misgegaan bij het bijwerken van de variabele.'
          );
          this._dialog.alert({ title, message, confirmText: 'OK' });
          return EMPTY;
        }),
        finalize(() => this.submitting.set(false))
      )
      .subscribe({
        next: () => {
          this._snackbar.open('Variabele bijgewerkt', 'OK');
          this.editingVarId.set(null);
        },
      });
  }

  handleDelete(v: VarResponse) {
    this._varService.getVarRefCount(v.id).subscribe((result) => {
      const { overrides, usages } = result;
      const hasRefs = overrides > 0 || usages > 0;

      let refInfo = '';
      if (hasRefs) {
        const parts: string[] = [];
        if (overrides > 0) {
          parts.push(
            `<b>${overrides}</b> cliënt-overschrijving${overrides === 1 ? '' : 'en'}`
          );
        }
        if (usages > 0) {
          parts.push(
            `<b>${usages}</b> interactie${usages === 1 ? '' : 's'} en/of template${usages === 1 ? '' : 's'} die deze variabele gebrui${usages === 1 ? 'kt' : 'ken'}`
          );
        }
        refInfo = ` Deze variabele heeft ${parts.join(' en ')}.`;
      }

      this._dialog.confirm({
        title: 'Variabele verwijderen',
        message:
          `Weet je zeker dat je de variabele <b>[${escapeHtml(v.key)}]</b> wilt verwijderen?${refInfo} ` +
          `De variabele wordt gedeactiveerd en na <b>10 dagen</b> definitief verwijderd, ` +
          `inclusief alle overschrijvingen. ` +
          `Tijdens deze periode kan de variabele nog worden hersteld.`,
        confirmText: 'Verwijderen',
        cancelText: 'Annuleren',
        onConfirm: () => this._performDelete(v.id),
      });
    });
  }

  handleRestore(v: VarResponse) {
    this._dialog.confirm({
      title: 'Variabele herstellen',
      message: `Wil je de variabele <b>[${escapeHtml(v.key)}]</b> herstellen? De variabele wordt weer actief en beschikbaar.`,
      confirmText: 'Herstellen',
      cancelText: 'Annuleren',
      onConfirm: () => this._performRestore(v.id),
    });
  }

  togglePending() {
    this.pendingExpanded.update((v) => !v);
  }

  handleSort({ active, direction }: Sort) {
    this.sortActive.set(active);
    this.sortDirection.set(direction);
    this.page.set(0);
  }

  handlePendingSort({ active, direction }: Sort) {
    this.pendingSortActive.set(active);
    this.pendingSortDirection.set(direction);
  }

  private _sortVars(
    vars: VarResponse[],
    active: string,
    direction: 'asc' | 'desc' | ''
  ): VarResponse[] {
    if (!active || !direction) {
      return [...vars].sort((a, b) => a.key.localeCompare(b.key));
    }

    const multiplier = direction === 'asc' ? 1 : -1;
    return [...vars].sort((a, b) => {
      switch (active) {
        case 'key':
          return multiplier * a.key.localeCompare(b.key);
        case 'value':
          return multiplier * a.value.localeCompare(b.value);
        case 'deleteAt':
          return (
            multiplier *
            (new Date(a.deleteAt ?? 0).getTime() - new Date(b.deleteAt ?? 0).getTime())
          );
        default:
          return 0;
      }
    });
  }

  formatDeleteAt(isoDate: string): string {
    const date = new Date(isoDate);
    return date.toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }

  private _performDelete(varId: string) {
    return this._varService.deleteVar(varId).pipe(
      catchError(() => {
        this._dialog.alert({
          title: 'Foutmelding',
          message: 'Er is iets misgegaan bij het verwijderen van de variabele.',
          confirmText: 'OK',
        });
        return EMPTY;
      })
    );
  }

  private _performRestore(varId: string) {
    return this._varService.restoreVar(varId).pipe(
      catchError(() => {
        this._dialog.alert({
          title: 'Foutmelding',
          message: 'Er is iets misgegaan bij het herstellen van de variabele.',
          confirmText: 'OK',
        });
        return EMPTY;
      })
    );
  }

  private _getErrorAlert(
    error: unknown,
    defaultMessage: string,
    errorMap: Record<string, { title: string; message: string }> = {}
  ): { title: string; message: string } {
    if (isHttpErrorResponse(error)) {
      const errorCode = error.error?.detail?.errorCode;
      if (errorCode && errorMap[errorCode]) {
        return errorMap[errorCode];
      }
    }
    return { title: 'Foutmelding', message: defaultMessage };
  }
}
